import React from 'react';
import { Form } from 'react-bootstrap';

import NedlagtDatoRow from './components/NedlagtDatoRow';
import KundenummerRow from './components/KundenummerRow';
import EnhetnavnRow from './components/EnhetnavnRow';
import GlnRow from './components/GlnRow';
import OrgNrRow from './components/OrgNrRow';
import { OrgNrInfo } from './components/OrgNrRow/types';
import ContactInfoRow from './components/ContactInfoRow';
import GrossistKundenummerRow from './components/GrossistKundenummerRow';

import { EndringsMelding } from 'services/QAApi/types';
import { EnhetViewModel, FirmaViewModel } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import { ChangeRequestCommand } from 'services/KjederegisteretAdminApi/ChangeRequestApi/types';

import { IFormData } from 'utils/hooks/useForm';

import Style from './index.module.css';
import KontonummerRow from './components/KontonummerRow';
import SalgsarealRow from './components/SalgsarealRow';
import OppstartdatoRow from './components/OppstartdatoRow';
// import FakturaEpostRow from "./components/FakturaEpostRow";
// import EpostRow from "./components/EpostRow";
import OmsetningRow from './components/OmsetningRow';

interface Props {
  formData: IFormData<ChangeRequestCommand, EnhetViewModel>;
  changeRequest: EndringsMelding;
  firma: FirmaViewModel;
  enhet: EnhetViewModel;
  duplicateWarning?: { orgnrDupes: string; nameDupes: string };
}

const QABasisForm: React.FC<Props> = (props) => {
  const handleUpdatedOrgNrInfo = (updatedInfo: OrgNrInfo) => {
    const copy = { ...props.formData.values };
    copy.organisasjonsnr = updatedInfo.organisasjonsnr;
    copy.isOrgnrValidated = updatedInfo.isOrgnrValidated;
    copy.juridisknavn = updatedInfo.juridisknavn;
    props.formData.updateValues(copy);
  };

  return (
    <div className={Style.basisFormContainer}>
      <Form>
        <NedlagtDatoRow
          value={props.formData.values.nedlagtDato}
          enhet={props.enhet}
          changeRequest={props.changeRequest.changes}
          onChange={(date) => props.formData.handleChange('nedlagtDato', date ? date : null)}
        />

        <KundenummerRow
          enhetId={props.formData.values.enhetId}
          leverandorKundenummer={props.enhet?.leverandorKundenummer}
          values={props.formData.values.leverandorKundenummer}
          onChange={(values) => props.formData.handleChange('leverandorKundenummer', values)}
          changeRequest={props.changeRequest.changes}
          isNedleggelse={!!props.changeRequest.changes.nedlagtDato}
        />

        <EnhetnavnRow
          enhetnavn={props.formData.values.enhetnavn}
          onChange={(updatedEnhetnavn) =>
            props.formData.handleChange('enhetnavn', updatedEnhetnavn)
          }
          enhetnavnFromCr={props.changeRequest.changes.enhetnavn}
          duplicateWarning={props.duplicateWarning?.nameDupes}
          error={
            props.formData.touched.enhetnavn && props.formData.errors.enhetnavn
              ? props.formData.errors.enhetnavn
              : undefined
          }
        />

        <GlnRow
          enhet={props.enhet}
          gln={props.formData.values.gln}
          glnFromCr={props.changeRequest.changes.gln}
          eierGLNNavn={props.firma?.eierGLNNavn}
          eierGLNNavnFromCr={props.changeRequest.changes.eierGLNNavn}
          sentBy={props.changeRequest.sentBy}
          sentBySystem={props.changeRequest.sentBySystem}
          onChange={(updatedGln) => props.formData.handleChange('gln', updatedGln)}
        />

        <OrgNrRow
          changeRequestId={props.changeRequest.id}
          incomingChangeRequest={{
            organisasjonsnr: props.changeRequest.changes.organisasjonsnr,
            juridisknavn: props.changeRequest.changes.juridisknavn,
          }}
          outgoingChangeRequest={{
            organisasjonsnr: props.formData.values.organisasjonsnr,
            isOrgnrValidated: props.formData.values.isOrgnrValidated,
            juridisknavn: props.formData.values.juridisknavn,
          }}
          enhetJurNavn={props.firma?.juridisknavn}
          duplicateWarning={props.duplicateWarning?.orgnrDupes}
          onChange={handleUpdatedOrgNrInfo}
        />

        <ContactInfoRow
          // Telefon
          telefon={props.formData.values.telefon}
          telefonFromCr={props.changeRequest.changes.telefon}
          onTelefonChange={(v) => props.formData.handleChange('telefon', v)}
          telefonError={props.formData.errors.telefon}
          // Epost
          epost={props.formData.values.epost}
          epostFromCr={props.changeRequest.changes.epost}
          onEpostChange={(v) => props.formData.handleChange('epost', v)}
          epostError={props.formData.errors.epost}
          // FakturaEpost
          fakturaEpost={props.formData.values.fakturaEpost}
          fakturaEpostFromCr={props.changeRequest.changes.fakturaEpost}
          onFakturaEpostChange={(v) => props.formData.handleChange('fakturaEpost', v)}
          fakturaEpostError={props.formData.errors.fakturaEpost}
          // Homepage
          homepage={props.formData.values.hjemmeside}
          homepageFromCr={(props.changeRequest.changes as any).hjemmeside}
          onHomepageChange={(v) => props.formData.handleChange('hjemmeside' as any, v)}
          homepageError={(props.formData.errors as any).hjemmeside}
          // Daglig leder
          dagligLeder={props.formData.values.dagligLeder as any}
          dagligLederFromCr={(props.changeRequest.changes as any).dagligLeder}
          onDagligLederChange={(v) => props.formData.handleChange('dagligLeder' as any, v)}
          dagligLederError={(props.formData.errors as any).dagligLeder}
        />

        <OmsetningRow
          value={props.formData.values.omsetning}
          onChange={(updatedValue) => props.formData.handleChange('omsetning', updatedValue)}
          omsetningFromCr={props.changeRequest.changes.omsetning}
          error={props.formData.errors.omsetning}
        />

        <KontonummerRow
          value={props.formData.values.kontonummer}
          onChange={(updatedValue) => props.formData.handleChange('kontonummer', updatedValue)}
          changeRequest={{
            kontonummer: props.changeRequest.changes.kontonummer,
            kontonummerFromCr: props.changeRequest.changes.kontonummer,
          }}
          error={props.formData.errors.kontonummer}
        />
        <SalgsarealRow
          value={props.formData.values.salgsareal}
          onChange={(updatedValue) => props.formData.handleChange('salgsareal', updatedValue)}
          changeRequest={{
            salgsareal: props.changeRequest.changes.salgsareal,
            salgsarealFromCr: props.changeRequest.changes.salgsareal,
          }}
          error={
            props.formData.touched.salgsareal && props.formData.errors.salgsareal
              ? props.formData.errors.salgsareal
              : undefined
          }
        />

        <OppstartdatoRow
          value={props.formData.values.oppstartsDato}
          onChange={(updatedValue) => props.formData.handleChange('oppstartsDato', updatedValue)}
          changeRequest={{
            oppstartsdato: props.changeRequest.changes.oppstartsDato,
            oppstartsdatoFromCr: props.changeRequest.changes.oppstartsDato,
          }}
          error={
            props.formData.touched.oppstartsdato && props.formData.errors.oppstartsdato
              ? props.formData.errors.oppstartsdato
              : undefined
          }
        />

        <GrossistKundenummerRow
          onChange={(updated, updateHovedGrossist) => {
            if (updateHovedGrossist) {
              const valueCopy = { ...props.formData.values };
              if (props.formData.values.hovedGrossist) {
                const copy = [
                  ...props.formData.values.andreGrossister,
                  props.formData.values.hovedGrossist,
                ].filter((x) => x.grossistGln !== updated.grossistGln);
                valueCopy.andreGrossister = copy;
              }
              valueCopy.hovedGrossist = updated;
              props.formData.updateValues(valueCopy);
            } else {
              const copy = [...props.formData.values.andreGrossister, updated];
              props.formData.handleChange('andreGrossister', copy);
            }
          }}
          enhetId={props.formData.values.enhetId}
          changeRequest={props.changeRequest.changes}
          hovedGrossist={props.formData.values.hovedGrossist}
          andreGrossister={props.formData.values.andreGrossister}
          error={
            props.formData.touched.grossist && props.formData.errors.grossist
              ? props.formData.errors.grossist
              : undefined
          }
        />
      </Form>
    </div>
  );
};

export default QABasisForm;
